import React, { useState, useEffect } from "react";
import {
  View,
  SafeAreaView,
  Platform,
  TouchableOpacity,
  Dimensions,
  Modal,
  Image,
  Text,
} from "react-native";
import StyleSheet from "react-native-media-query";
import DeviceInfo from "react-native-device-info";
import {
  Logger,
  getUserProfile,
  userDisplayName,
  getUserEmail,
} from "@lib/utilities/HelperFunctions";
import RightPanelCommonView from "./RightPanelCommonView";
import Color from "../../assets/color/Color";
import {
  FAQConstants,
  getCompanyName,
  isCustomDrawerEnabled,
  NavigationScreenName,
} from "@lib/utilities/Constants";
import { useAppSelector } from "@lib";
import { theme } from "../../theme/theme";
import CustomDrawer from "../custom-drawer/CustomDrawer";
import FaqSection from "../faq-section/FaqSection";
import { BACK_ICON_WHITE } from "src/resources/IMAGES";
import { FaqJsonData } from "src/resources/strings";

const Rightpanel = (props: any) => {
  const [userName, setUserName] = useState("");
  const [userCredential, setUserCredential] = useState("");
  const [displayManageAccountView, setDisplayManageAccountView] =
    useState(false);
  const [faqVisible, setFaqVisible] = useState(false);
  const [email, setEmail] = useState(``);
  const { height } = Dimensions.get("window");
  const user = useAppSelector((state) => state.userReducer);
  const firstName:string|null = user?.profile?.first_name;

  const companyName = getCompanyName()?.toUpperCase();

  useEffect(() => {
    const setup = async () => {
      let displayName: any = await userDisplayName(user);
      if (!displayName) {
        displayName = await getUserProfile();
      }
      setUserName(displayName);
      getUsernameInitials(displayName);
      const userEmail = await getUserEmail();
      setEmail(userEmail);
    };

    setup();

    const handleOrientationChange = () => {
      Logger(DeviceInfo.isLandscape());
    };

    // Add event listener for orientation change
    const subscription = Dimensions.addEventListener(
      "change",
      handleOrientationChange,
    );

    // Cleanup on unmount
    return () => {
      subscription?.remove();
    };
  }, []);

  const getUsernameInitials = async (displayName: string) => {
    // @ts-ignore
    let rgx = new RegExp(/(\p{L}{1})\p{L}+/, "gu");
    // @ts-ignore
    let initials: any = [...displayName?.matchAll(rgx)] || [];
    initials = (
      (initials.shift()?.[1] || "") + (initials.pop()?.[1] || "")
    ).toUpperCase();
    setUserCredential(initials);
  };

  const onManageAccountClick = async () => {
    setDisplayManageAccountView(true);
  };

  const handleFaqPress = () => {
    setFaqVisible(true);
  };

  const moveToDeleteAccountScreen = () => {
    setDisplayManageAccountView(false);
    props.closeRightPanel();
    props.navigation.navigate(NavigationScreenName.deleteAccount);
  };

  const moveToResetPasswordScreen = () => {
    props.closeRightPanel();
    props.navigation.navigate(NavigationScreenName.resetPassword);
  };

  const BackBtn = () => (
    <TouchableOpacity
      onPress={() => {
        setFaqVisible(false);
      }}
      testID="side-panel-back-button"
      style={styles.backButton}
    >
      <Image
        style={styles.backButton}
        dataSet={{ media: ids.backButton }}
        source={BACK_ICON_WHITE}
        testID="side-panel-back-icon"
      />
    </TouchableOpacity>
  );

  const FaqTopSection = () => (
    <View style={styles.faqSection}>
      <View style={styles.row}>
        <BackBtn />
        <Text style={styles.drawerTitle}>FAQs</Text>
      </View>
      <View style={styles.subTitleContianer}>
        <Text style={styles.faqSubtitle} dataSet={{ media: ids.faqSubtitle }}>
          Hi {firstName}! {FAQConstants.howCanWeHelp}
        </Text>
      </View>
    </View>
  );

  return (
      <Modal
        visible={props.isModalVisible}
        animationType="none"
        transparent={true}
      >
        <SafeAreaView style={[styles.containerFluid]}>
          <View style={styles.rightPanel} dataSet={{ media: ids.rightPanel }}>
            {isCustomDrawerEnabled ? (
              <CustomDrawer
                moveToResetPasswordScreen={moveToResetPasswordScreen}
                moveToDeleteAccountScreen={moveToDeleteAccountScreen}
                closeRightPanel={props.closeRightPanel}
                userCredential={userCredential}
                userName={userName}
                email={email}
              />
            ) : (
              <>
                {faqVisible ? (
                  <>
                    <FaqTopSection />

                    <FaqSection
                      data={FaqJsonData[companyName]}
                      onNavigate={(href) => {
                        if (href === "Reset Password") {
                          props.closeRightPanel();
                          navigation.navigate(
                            NavigationScreenName.resetPassword,
                          );
                        }
                      }}
                    />
                  </>
                ) : (
                  <RightPanelCommonView
                    onManageAccountClick={onManageAccountClick}
                    moveToResetPasswordScreen={moveToResetPasswordScreen}
                    moveToDeleteAccountScreen={moveToDeleteAccountScreen}
                    setDisplayManageAccountView={setDisplayManageAccountView}
                    closeRightPanel={props.closeRightPanel}
                    displayManageAccountView={displayManageAccountView}
                    userCredential={userCredential}
                    userName={userName}
                    email={email}
                    screenName={props.screenName}
                    height={height}
                    onFaqPress={handleFaqPress}
                    faqVisible={faqVisible}
                  />
                )}
              </>
            )}
          </View>
          <TouchableOpacity
            onPress={() => {
              props.closeRightPanel();
            }}
            style={styles.overlay}
          >
            <View
              style={styles.overlay}
              dataSet={{ media: ids.overlay }}
            ></View>
          </TouchableOpacity>
        </SafeAreaView>
      </Modal>
  );
};

const { ids, styles } = StyleSheet.create({
  containerFluid: {
    position: "absolute",
    left: 0,
    top: 0,
    right: 0,
    bottom: 0,
    // minHeight: 900, full screen height issue fix
    display: "flex",
    flexDirection: "row",
    justifyContent: "flex-end",
  },
  rightPanel: {
    backgroundColor: theme.colors.white,
    height: "100%",
    zIndex: 2,
    ...Platform.select({
      ios: {
        width: "100%",
        "@media (min-width: 200px)": {
          width: "80%",
        },
        "@media (min-width: 380px)": {
          width: "80%",
          paddingTop: 30,
        },
        "@media (min-width: 700px)": {
          width: "60%",
          paddingTop: 0,
        },
        height: "110%",
      },
      android: {
        width: "100%",
        "@media (min-width: 200px)": {
          width: "100%",
        },
        "@media (min-width: 380px)": {
          width: "100%",
          paddingTop: 30,
        },
        "@media (min-width: 700px)": {
          width: "65%",
          paddingTop: 0,
        },
        height: "100%",
      },
      web: {
        "@media (min-width: 200px)": {
          width: "100%",
        },
        "@media (min-width: 700px)": {
          width: "60%",
        },
        "@media (min-width: 1000px)": {
          width: "55%",
        },
        "@media (min-width: 1200px)": {
          width: "40%",
        },
        "@media (min-width: 1400px)": {
          width: "30%",
        },
        height: "100%",
      },
    }),
  },
  overlay: {
    position: "absolute",
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: theme.colors.black + 50,
    zIndex: 1,
  },
  faqSection: {
    backgroundColor: theme.colors.primary,
    padding: theme.spacing.md,
  },
  subTitleContianer: {
    height: 100,
  },
  row: {
    flexDirection: "row",
    alignItems: "center",
    gap: theme.spacing.xlg,
  },
  faqSubtitle: {
    fontFamily: theme.fontFamily.robotoBold,
    fontWeight: theme.fontweight.bold,
    fontSize: theme.fontsize.cs,
    margin: "auto",
    color: theme.colors.white,
  },
  drawerTitle: {
    fontFamily: theme.fontFamily.robotoSlabBold,
    fontSize: theme.fontsize.mmd,
    color: theme.colors.white,
  },
  backButton: {
    resizeMode: "contain",
    padding:theme.spacing.ssm,
    paddingHorizontal:theme.spacing.sm,
  },
});
export default Rightpanel;
